import { Box, FormControl } from '@chakra-ui/react';
import {
  createFilter,
  type GroupBase,
  Select,
  type SingleValue,
} from 'chakra-react-select';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'urql';

import { graphql, type User } from '@revelio/data-access';

import { THEMED_SELECT_CLASS_NAME_PREFIX } from '../../components/themed-select/themedSelectClassNamePrefix';
import { PreferenceEditorSectionHeading } from '../components/PreferenceEditorSectionHeading';
import { PreferenceScope, UserPreferenceKey } from '../types';
import { PreferenceBrowserStorage } from '../utils/preference-session-storage';
import type {
  PreferenceEditorProps,
  PreferenceMeta,
} from './preferenceEditors';

type CurrencyOption = {
  id: string;
  label: string;
  value: string;
  code: string;
  rate: number;
  record_date: string;
  effective_date: string;
};

const CurrencySelectionList = graphql(`
  query CurrencySelectionList {
    selectionList {
      currency {
        id
        code
        name
        rate
        record_date
        effective_date
      }
    }
  }
`);

const currencyDataToOption = (data: User['currency_data']): CurrencyOption => {
  return {
    id: data?.id ?? '',
    label: data?.name?.replace('-', ' ') ?? '',
    value: data?.name ?? '',
    code: data?.code ?? '',
    rate: data?.rate ?? 0,
    record_date: data?.record_date ?? '',
    effective_date: data?.effective_date ?? '',
  };
};

const meta: PreferenceMeta = {
  id: UserPreferenceKey.Currency,
  label: 'Currency',
};

const filterOption = createFilter<CurrencyOption>({
  stringify: (option) =>
    `${option.data.label} ${option.data.code} ${option.data.value}`,
});

function CurrencyEditor({
  onPreferenceUpdate,
  preferences,
  diff,
}: PreferenceEditorProps) {
  const [selected, setSelected] = useState<CurrencyOption | undefined>();

  const [{ data, fetching }] = useQuery({
    query: CurrencySelectionList,
  });

  const options = useMemo<CurrencyOption[]>(() => {
    const list = data?.selectionList?.currency ?? [];
    const opts = list
      .map(currencyDataToOption)
      .filter((o: CurrencyOption) => !!o.id);
    return opts.sort((a: CurrencyOption, b: CurrencyOption) =>
      a.label.localeCompare(b.label)
    );
  }, [data]);

  useEffect(() => {
    const preference = preferences[UserPreferenceKey.Currency];
    const id = preference?.id;
    const code = preference?.code;
    if (!id && !code) return;
    // Reverse options to reach first US dollar instead of Ecuadorian Dollar
    const match =
      options.find((o) => o.id === id) ??
      [...options].reverse().find((o) => o.code === code);
    setSelected(match);
  }, [preferences, options]);

  const onChange = useCallback(
    (opt: CurrencyOption | null) => {
      if (!opt) return;
      const value = { id: opt.id, code: opt.code, rate: opt.rate };
      setSelected(opt);
      onPreferenceUpdate(UserPreferenceKey.Currency, value);
      PreferenceBrowserStorage.update(
        PreferenceScope.GLOBAL,
        UserPreferenceKey.Currency,
        value
      );
    },
    [onPreferenceUpdate]
  );

  return (
    <FormControl data-testid="currency-editor-select">
      <PreferenceEditorSectionHeading
        title="Currency"
        indicateDiff={diff?.includes(meta.id) ?? false}
        tooltip="The currency used to display compensation in dashboards."
      />
      <Box data-testid="currency-select">
        <Select<CurrencyOption, false, GroupBase<CurrencyOption>>
          classNamePrefix={THEMED_SELECT_CLASS_NAME_PREFIX}
          size="sm"
          menuPlacement="auto"
          isLoading={fetching}
          options={options}
          value={selected}
          onChange={(val: SingleValue<CurrencyOption>) => onChange(val)}
          filterOption={filterOption}
          placeholder="Select currency..."
        />
      </Box>
    </FormControl>
  );
}

export { CurrencyEditor as default, meta };
